import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  lazy,
  Suspense,
  useMemo
} from "react";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import Style from "./../../assets/scss/custom.module.scss";
import "./customerstyle.scss";
import privateAxios from "../../utils/axiosPrivate";
import { CUSTOMER } from "../../utils/constants/appConstants";
import { useParams, useSearchParams } from "react-router-dom";
import useXeroApi from "../../hooks/useXeroApi";
import Details from "./customer-tab/details/CustomerDetails";
import SystemControl from "./customer-tab/system-control/SystemControl";
import DirectDebit from "./customer-tab/direct-debit/DirectDebit";
import UsercontactDetails from "./customer-tab/contact-details/UsercontactDetails";
import Marketing from "./customer-tab/marketing/Marketing";
import CustomerCommentModal from "../../components/modals/CustomerCommentModal";
import CustomerLog from "./customer-tab/customer-log/CustomerLog";
import { formatDateWithDayjs } from "../../utils/utils";
import NotFound from "../NotFound";
import { customersDtls, dashboard } from "../../locales/en";
import { useDispatch, useSelector } from "react-redux";
import { getUsersData } from "../../redux/userManagementSlice";
import { getConnectedSoftware } from "../../redux/loginSlice";
import {
  getMyobBankAccountsURL,
  getQuickbooksBankAccountsURL,
  getReckonBankAccountsURL,
  getXeroBankAccountsURL
} from "../../utils/constants/apiEndPoints";
import UserDataSkeleton from "../../components/skeletons/UserDataSkeleton";
import ChecklistData from "./checklist-tab";
import ClientDetailsSkeleton from "../../components/skeletons/ClientDetailsSkeleton";
import FinancialTabSkeleton from "../../components/skeletons/FinancialTabSkeleton";
import OwnerTabSkeleton from "../../components/skeletons/OwnerTabSkeleton";
import ErrorBoundary from "../../components/error-boundary";
import ComponentViewFallback from "../../components/error-boundary/fallback/ComponentViewFallback";
import { useToast } from "../../hooks/useToast";

const CustomerDetails = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const InvoTabdata = lazy(() => import("./invoice-tab/InvoTabdata"));
  const FinancialTab = lazy(() => import("./financials-tab"));
  const OwnerTab = lazy(() => import("./owner-tab"));
  const TrustandCorporateTab = lazy(() => import("./trustandcorporate-tab"));
  const CustomreHeader = lazy(
    () => import("./../../components/customer-details-headers/index")
  );
  // usersLoading use this for loding
  const { usersData, usersLoading, notFoundUserData } = useSelector(
    (state) => state.userManagement
  );
  const { refreshPopupCommentsFlag } = useSelector(
    (state) => state.clientDetails
  );
  const { CustomerdetailsXeroRefresh } = useXeroApi();

  const [showApprovePassword, setShowApprovePassword] = useState([]);
  const [key, setKey] = useState(CUSTOMER);
  const [sendData, setSendData] = useState({});

  // To set Owner Tab Active From Request made from Customer List
  const [searchParams] = useSearchParams();
  const [initialTabSet, setInitialTabSet] = useState(false);
  const { id } = useParams();
  const firstLoad = useRef(true);

  const [commentModal, setCommentModal] = useState(true);
  const isFirstCommentRefresh = useRef(true);

  const handleHideCommentModal = () => {
    setCommentModal(!commentModal);
  };

  useEffect(() => {
    if (isFirstCommentRefresh.current) {
      isFirstCommentRefresh.current = false;
      return;
    }
    setCommentModal(true);
  }, [refreshPopupCommentsFlag]);

  const [bankName, setBankName] = useState({});

  // for user data fetch
  useEffect(() => {
    const handleUsersGetData = async () => {
      try {
        if (usersData && usersData?.id === Number(id)) {
          setSendData(usersData);
        } else {
          const response = await dispatch(getUsersData({ id })).unwrap();
          const { data } = response.data;
          setSendData(data);
        }
      } catch {
        // handle catch here;
      }
    };
    if (id) {
      handleUsersGetData();
    }
  }, [id, usersData]);

  // for bank details details get
  const handlePostReckonBankData = useCallback(async () => {
    try {
      const response = await privateAxios.post(getReckonBankAccountsURL);
      setBankName(response.data.data);
    } catch {
      // handle catch here;
    }
  }, []);

  const handlePostXeroBankData = useCallback(async () => {
    try {
      const response = await privateAxios.get(getXeroBankAccountsURL(id));
      setBankName(response.data.data);
    } catch {
      // handle catch here;
    }
  }, [id]);

  const handlePostMyobBankData = useCallback(async () => {
    try {
      const response = await privateAxios.get(getMyobBankAccountsURL);
      setBankName(response.data.data);
    } catch {
      //handle catch here;
    }
  }, []);

  const handlePostQuickBooksBankData = useCallback(async () => {
    try {
      const response = await privateAxios.get(getQuickbooksBankAccountsURL);
      setBankName(response.data.data);
    } catch {
      //handle catch here;
    }
  }, []);

  const handlePostBankData = useCallback(
    async (accountingSoftwareType) => {
      try {
        switch (accountingSoftwareType) {
          case "Reckon":
            await handlePostReckonBankData();
            break;
          case "MYOB":
            await handlePostMyobBankData();
            break;
          case "Xero":
            await handlePostXeroBankData();
            break;
          case "QuickBooks":
            await handlePostQuickBooksBankData();
            break;
          default:
            throw new Error(customersDtls.errMsg.item2);
        }
      } catch {
        //handle catch here;
      }
    },
    [
      handlePostReckonBankData,
      handlePostMyobBankData,
      handlePostXeroBankData,
      handlePostQuickBooksBankData
    ]
  );

  // Check Account Connection Status
  const [accSoftwareConct, setAccSoftwareConct] = useState("");
  const [accSoftwareIsConnct, setAccSoftwareIsConnct] = useState(null);
  const [accSoftwareIsConnctData, setAccSoftwareIsConnctData] = useState({});
  const [accSoftwareId, setAccSoftwareId] = useState(null);

  const handleCheckAccSftConnection = useCallback(
    async (isRefreshed = false) => {
      try {
        const payload = {
          userAccountId: id,
          ...(isRefreshed && { isRefreshed: true })
        };

        const response = await dispatch(getConnectedSoftware(payload)).unwrap();

        const {
          isConnected,
          disconnectedAt,
          accountingSoftwareType,
          softwareId
        } = response?.data?.data ?? {};

        setAccSoftwareIsConnct(isConnected);
        setAccSoftwareIsConnctData(response?.data?.data);
        setAccSoftwareId(softwareId);

        if (softwareId && isConnected) {
          await handlePostBankData(accountingSoftwareType);
        }

        if (isConnected) {
          setAccSoftwareConct(accountingSoftwareType);
        } else if (isConnected === undefined) {
          setAccSoftwareConct(accountingSoftwareType);
        } else {
          const formattedDate = formatDateWithDayjs(disconnectedAt);
          setAccSoftwareConct(
            `${customersDtls?.disconnected} (${accountingSoftwareType} ${customersDtls?.at} ${formattedDate})`
          );
        }

        if (isRefreshed) {
          toast.success(response?.data?.message);
        }
      } catch (error) {
        toast.error(error?.response?.data?.message);
      }
    },
    [id]
  );

  useEffect(() => {
    handleCheckAccSftConnection();
  }, [handleCheckAccSftConnection]);

  useEffect(() => {
    // To set Owner Tab Active From Request made from Customer List
    if (!initialTabSet && searchParams.get("from") === "customerList") {
      setKey("Owner");
      setInitialTabSet(true);
    }
    // To set Owner Tab Active From Request made from Customer List
    if (!initialTabSet && searchParams.get("from") === "invoice") {
      setKey("Financials");
      setInitialTabSet(true);
    }
    // To set Trust Cororate From Request made from Directorship Add Corporate
    if (
      !initialTabSet &&
      searchParams.get("from") === "directorshipAddCorporate"
    ) {
      setKey("Trust&Corporates");
      setInitialTabSet(true);
      // Remove the ?from=directorshipAddCorporate param after 2 seconds
      setTimeout(() => {
        const url = new URL(window.location.href);
        url.searchParams.delete("from");
        window.history.replaceState({}, "", url.pathname + url.search);
      }, 2000);
    }
  }, [searchParams, initialTabSet]);

  const handleOnRefresh = useCallback(async () => {
    try {
      const response = await CustomerdetailsXeroRefresh();
      toast.success(response?.data?.message);
    } catch (error) {
      toast.error(error?.response?.data?.message);
    }
  }, [CustomerdetailsXeroRefresh]);

  useEffect(() => {
    if (usersLoading && firstLoad.current) {
      firstLoad.current = false;
    }
  }, [usersLoading]);

  // for tab data
  const MemoizedInvoiceTab = useMemo(
    () => (
      <Suspense fallback={null}>
        <InvoTabdata setActiveTab={setKey} />
      </Suspense>
    ),
    []
  );

  const MemoizedFinancialTab = useMemo(
    () => (
      <Suspense fallback={<FinancialTabSkeleton />}>
        <FinancialTab accSoftwareConct={accSoftwareConct} />
      </Suspense>
    ),
    [accSoftwareConct]
  );

  const MemoizedOwnerTab = useMemo(
    () => (
      <Suspense fallback={<OwnerTabSkeleton />}>
        <OwnerTab setShowApprovePassword={setShowApprovePassword} />
      </Suspense>
    ),
    []
  );

  const MemoizedTrustCorporateTab = useMemo(
    () => (
      <Suspense fallback={<UserDataSkeleton />}>
        <TrustandCorporateTab />
      </Suspense>
    ),
    []
  );

  const MemoizedCustomreHeader = useMemo(
    () => (
      <Suspense fallback={null}>
        <CustomreHeader
          handleOnRefresh={handleOnRefresh}
          showApprovePassword={showApprovePassword}
        />
      </Suspense>
    ),
    []
  );

  if (usersLoading || (usersData?.id !== Number(id) && !notFoundUserData)) {
    return <ClientDetailsSkeleton />;
  }

  if (notFoundUserData || usersData?.primaryUserId) {
    return <NotFound />;
  }

  return (
    <>
      <div className="customer-details-top-header mt-2 mb-3">
        <ErrorBoundary fallback={<ComponentViewFallback />}>
          {MemoizedCustomreHeader}
        </ErrorBoundary>
      </div>
      <div className={`${Style.tabContainerCustom} tabcontainercustom`}>
        <Tabs
          id=""
          activeKey={key}
          onSelect={(k) => setKey(k)}
          className="mb-3"
          mountOnEnter
          unmountOnExit={false}
        >
          {/* Tab content for Customer */}
          <Tab eventKey="Customer" title={customersDtls?.tabs.title1}>
            {/* {
                  usersLoading ? <UserDataSkeleton lines={15} /> : */}
            <div className="container-fluid p-0">
              <div className="row">
                <div className="col-lg-6 mb-3">
                  <div className="customerdetails-section mb-3">
                    <ErrorBoundary fallback={<ComponentViewFallback />}>
                      <Details
                        accSoftwareConct={accSoftwareConct}
                        accSoftwareIsConnct={accSoftwareIsConnct}
                        onRefreshSoftware={handleCheckAccSftConnection}
                      />
                    </ErrorBoundary>
                  </div>
                  <div className="systemcontrol-section mb-3">
                    <ErrorBoundary fallback={<ComponentViewFallback />}>
                      <SystemControl />
                    </ErrorBoundary>
                  </div>
                  <div className="directdebit-section mb-3">
                    <ErrorBoundary fallback={<ComponentViewFallback />}>
                      <DirectDebit
                        accSoftwareIsConnctData={accSoftwareIsConnctData}
                        bankName={bankName}
                        accSoftwareId={accSoftwareId}
                      />
                    </ErrorBoundary>
                  </div>
                </div>
                <div className="col-lg-6 mb-3">
                  <div className="contactdetails-section mb-3">
                    <ErrorBoundary fallback={<ComponentViewFallback />}>
                      <UsercontactDetails />
                    </ErrorBoundary>
                  </div>
                  <div className="marketing-section mb-3">
                    <ErrorBoundary fallback={<ComponentViewFallback />}>
                      <Marketing />
                    </ErrorBoundary>
                  </div>
                  <div className="marketing-section mb-3">
                    {/* <TrakingDetails /> */}
                  </div>
                  <div className="customerlog-section mb-3">
                    <ErrorBoundary fallback={<ComponentViewFallback />}>
                      <CustomerLog />
                    </ErrorBoundary>
                  </div>
                </div>
              </div>
            </div>
            {/* } */}
          </Tab>

          {/* Tab content for Invoices */}
          <Tab eventKey="Invoices" title={dashboard?.invoiceTabName}>
            <ErrorBoundary fallback={<ComponentViewFallback />}>
              <div className="container-fluid p-0">
                <div className="row">
                  <div className="col-lg-12">
                    <div className="customerdetails-section mb-3">
                      {MemoizedInvoiceTab}
                    </div>
                  </div>
                </div>
              </div>
            </ErrorBoundary>
          </Tab>

          {/* Tab content for Financials */}
          <Tab eventKey="Financials" title={customersDtls?.tabs.title3}>
            <ErrorBoundary fallback={<ComponentViewFallback />}>
              {MemoizedFinancialTab}
            </ErrorBoundary>
          </Tab>

          {/* Tab content for Owner */}
          <Tab eventKey="Owner" title={customersDtls?.tabs.title4}>
            <ErrorBoundary fallback={<ComponentViewFallback />}>
              {MemoizedOwnerTab}
            </ErrorBoundary>
          </Tab>

          {/* Tab content for Trust & Corporates */}
          <Tab eventKey="Trust&Corporates" title={customersDtls?.tabs.title5}>
            <ErrorBoundary fallback={<ComponentViewFallback />}>
              {MemoizedTrustCorporateTab}
            </ErrorBoundary>
          </Tab>

          {/* Tab content for Checklist */}
          <Tab eventKey="Checklist" title={customersDtls?.tabs.title6}>
            <ErrorBoundary fallback={<ComponentViewFallback />}>
              <ChecklistData />
            </ErrorBoundary>
          </Tab>
        </Tabs>
      </div>
      <CustomerCommentModal
        isOpen={commentModal}
        toggle={handleHideCommentModal}
        sendData={sendData}
      />
    </>
  );
};

export default CustomerDetails;
